import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, X, ChevronLeft, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';

import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const stripePromiseCache = new Map();
function getStripePromise(publishableKey) {
  if (!publishableKey) return null;
  if (!stripePromiseCache.has(publishableKey)) {
    stripePromiseCache.set(publishableKey, loadStripe(publishableKey));
  }
  return stripePromiseCache.get(publishableKey);
}

export function VoucherCheckout({ product, open, onClose }) {
  const { t, language } = useLanguage();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [recipientName, setRecipientName] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [honeypot, setHoneypot] = useState('');

  const [clientSecret, setClientSecret] = useState('');
  const [publishableKey, setPublishableKey] = useState('');

  const stripePromise = useMemo(() => getStripePromise(publishableKey), [publishableKey]);

  function reset() {
    setStep(1);
    setSubmitting(false);
    setError('');
    setRecipientName('');
    setPersonalMessage('');
    setBuyerName('');
    setBuyerEmail('');
    setAcceptedTerms(false);
    setClientSecret('');
    setPublishableKey('');
  }

  function handleClose() {
    onClose();
    setTimeout(reset, 300);
  }

  function canProceedStep1() {
    return recipientName.trim().length >= 1;
  }

  function canProceedStep2() {
    return (
      buyerName.trim().length >= 1 &&
      /\S+@\S+\.\S+/.test(buyerEmail) &&
      acceptedTerms
    );
  }

  async function handleStartCheckout() {
    if (!product) return;
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/vouchers/checkout`, {
        product_id: product.id,
        recipient_name: recipientName.trim(),
        buyer_name: buyerName.trim(),
        buyer_email: buyerEmail.trim(),
        personal_message: personalMessage.trim(),
        language,
        honeypot,
      });
      if (!data.client_secret || !data.publishable_key) {
        throw new Error('missing_session');
      }
      setPublishableKey(data.publishable_key);
      setClientSecret(data.client_secret);
      setStep(3);
    } catch (e) {
      setError(t.vouchers.errors.payment);
    } finally {
      setSubmitting(false);
    }
  }

  if (!product) return null;

  const productCopy = t.vouchers.products[product.id] || { title: '', subtitle: '' };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className="max-w-xl p-0 overflow-hidden bg-warm-50"
        data-testid="voucher-checkout-dialog"
      >
        <DialogTitle className="sr-only">{t.vouchers.buy}</DialogTitle>
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-200 bg-white">
          <div className="flex items-center gap-3">
            {step > 1 && step < 3 && (
              <button
                onClick={() => setStep(step - 1)}
                className="text-warm-600 hover:text-accent"
                aria-label="back"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <div className="text-[10px] tracking-[2px] uppercase text-warm-500">
                {productCopy.title}
              </div>
              <div className="font-heading text-warm-900 text-lg">
                €{product.price_eur} · {productCopy.subtitle}
              </div>
            </div>
          </div>
          <button onClick={handleClose} className="text-warm-600 hover:text-accent" aria-label="close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="space-y-5"
              >
                <h3 className="font-heading text-xl text-warm-900">{t.vouchers.checkout.step1}</h3>

                <div className="space-y-2">
                  <Label htmlFor="recipient-name">{t.vouchers.checkout.recipientName} *</Label>
                  <Input
                    id="recipient-name"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder={t.vouchers.checkout.recipientNamePlaceholder}
                    maxLength={80}
                    data-testid="recipient-name-input"
                  />
                  <p className="text-xs text-warm-500">{t.vouchers.checkout.recipientHelp}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="personal-message">{t.vouchers.checkout.message}</Label>
                  <Textarea
                    id="personal-message"
                    value={personalMessage}
                    onChange={(e) => setPersonalMessage(e.target.value)}
                    placeholder={t.vouchers.checkout.messagePlaceholder}
                    maxLength={400}
                    rows={3}
                  />
                </div>

                <Button
                  onClick={() => setStep(2)}
                  disabled={!canProceedStep1()}
                  className="w-full bg-accent hover:bg-accent-dark text-white rounded-full"
                  data-testid="checkout-step1-continue"
                >
                  {t.vouchers.checkout.continue}
                </Button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="space-y-5"
              >
                <h3 className="font-heading text-xl text-warm-900">{t.vouchers.checkout.step2}</h3>

                <div className="space-y-2">
                  <Label htmlFor="buyer-name">{t.vouchers.checkout.buyerName} *</Label>
                  <Input
                    id="buyer-name"
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    maxLength={80}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="buyer-email">{t.vouchers.checkout.buyerEmail} *</Label>
                  <Input
                    id="buyer-email"
                    type="email"
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    maxLength={120}
                  />
                  <p className="text-xs text-warm-500">{t.vouchers.checkout.emailHelp}</p>
                </div>

                <div style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }} aria-hidden="true">
                  <Input
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <Checkbox
                    id="accept-terms"
                    checked={acceptedTerms}
                    onCheckedChange={(v) => setAcceptedTerms(!!v)}
                  />
                  <label htmlFor="accept-terms" className="text-sm text-warm-700 leading-relaxed cursor-pointer">
                    {t.vouchers.checkout.acceptTerms}
                    <a href="#voucher-terms" className="underline text-accent">
                      {t.vouchers.checkout.termsLink}
                    </a>
                    .
                  </label>
                </div>

                {error && (
                  <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 p-3 rounded">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  onClick={handleStartCheckout}
                  disabled={!canProceedStep2() || submitting}
                  className="w-full bg-accent hover:bg-accent-dark text-white rounded-full"
                  data-testid="checkout-step2-continue"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t.vouchers.checkout.processing}
                    </>
                  ) : (
                    `${t.vouchers.checkout.payAmount} €${product.price_eur}`
                  )}
                </Button>
              </motion.div>
            )}

            {step === 3 && clientSecret && stripePromise && (
              <motion.div
                key="step3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div id="checkout" className="min-h-[480px]">
                  <EmbeddedCheckoutProvider
                    stripe={stripePromise}
                    options={{ clientSecret }}
                  >
                    <EmbeddedCheckout />
                  </EmbeddedCheckoutProvider>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
